import { Box, Drawer, drawerClasses, styled, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import classNames from "classnames";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ProductService } from "~/AddToCart/ProductService";
import type { AddToCartFormData, OptionalPartFormData } from "~/AddToCart/hooks/useAddToCartNew";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { type AddToCartApiItem, type AddToCartItem, type AddToCartPartItem } from "~/Cart/api/AddToCart";
import { Button } from "~/common/components/Button/Button";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import { LinkButton } from "~/common/components/Link";
import Spinner from "~/common/components/Spinner/Spinner";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { C_OUTLINE_VARIANT } from "~/common/components/icon/Colors";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { ImageFormat } from "~/dto/ProductDataDTO";
import { isKITProduct, type ProductData } from "~/models/ProductData";
import type { RootState } from "~/redux/store";
import { formatCurrency } from "~/utils/currencyFormatter";
import { userIsCustomer } from "~/utils/roles";
import classes from "./CartSuccessModal.module.less";
import LanguageAwareExternalContent from "~/Configurators/Linear/components/LanguageAwareExternalContent";
import CenterContents from "~/common/components/CenterContents";
import { volatileSum, zero } from "~/utils/PriceUtils";
import { Translation } from "~/common/Translation";
import { useMultipleRelatedProducts } from "~/redux/products/relatedProducts/useRelatedProducts";
import { IMG_MISSING_PRODUCT } from "~/utils/url/staticResources";
import { resolveCartPageUrl } from "~/utils/url/urlUtils";
import { useMultipleTopsellerProducts } from "~/redux/products/topsellerProducts/useTopsellerProducts";
import { Recommendations } from "~/AddToCart/components/AddToCartProcess/Modals/CartSuccessModal/Recommendations";
import { makeStyles } from "@mui/styles";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { calculateSalesCampaign4PDP } from "~/SalesCampaign/utils";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { Alert } from "~/common/m3/Alert/Alert";
import type { PurchasePlace } from "~/feature/Analytics/ga/recommendations/types";
import { isTariffSurchargeEnabled } from "~/Cart/cartUtils";

export type ExtendedAddToCartFormData = AddToCartFormData & { productCode: string; saleNetPrice?: number };

interface CartSuccessModalProps {
  onClose: () => void;
  formData: ExtendedAddToCartFormData[] | AddToCartItem[] | AddToCartApiItem[];
  linearConfig?: ProductData;
  className?: string;
  page: PurchasePlace;
}

const SummaryLabel = ({ label, value, className }: { label: ReactNode; value: ReactNode; className?: string }) => {
  return (
    <div className={classNames("d-flex flex-justify-between", className)}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
};

const SingleEntry = ({
  formData,
  productInfo,
  isTablet,
  isLinear,
  hasRelatedProducts,
}: {
  formData: ExtendedAddToCartFormData[] | AddToCartItem[];
  productInfo: ProductData;
  isTablet: boolean;
  hasRelatedProducts: boolean;
  isLinear?: boolean;
}) => {
  const { t } = useTranslation();
  const isBusiness = userIsCustomer();

  const productDescriptor = ProductService.useProductDescriptor({
    productCode: productInfo.code ?? "",
    priceReference: formData[0]?.priceReference ?? "",
  });

  if (!productInfo) {
    return (
      <CenterContents>
        <CircleSpinner />
      </CenterContents>
    );
  }

  const isHousingKit = isKITProduct(productInfo);

  let netPrice: number | null;
  let basePrice: number | null = null;

  let hasSales = false;

  const extendedCartItem = formData[0] as ExtendedAddToCartFormData;
  const tariffSurcharge = isTariffSurchargeEnabled() && extendedCartItem.tariffSurcharge;

  const salesData = calculateSalesCampaign4PDP({ productDescriptor });
  const selectedPackCode = extendedCartItem.selectedPack?.packCode ?? INVALID_PACKAGE_CODE;

  hasSales = !!salesData.data?.salePerPack[selectedPackCode];

  if (formData[0].saleNetPrice) {
    hasSales = true;
    netPrice = formData[0].saleNetPrice;
    if (formData[0].netPrice && typeof formData[0].netPrice !== "number") {
      basePrice = formData[0].netPrice.value;
    }
  } else if (formData[0].netPrice && typeof formData[0].netPrice !== "number") {
    netPrice = formData[0].netPrice.value;
  } else {
    netPrice = formData[0].netPrice;
  }

  if (netPrice !== null && isHousingKit) {
    let optionalPartsPrice = 0;
    if (Array.isArray(formData[0].optionalParts)) {
      const optionalParts = formData[0].optionalParts as AddToCartPartItem[];
      optionalPartsPrice = volatileSum(
        ...optionalParts.map((part) => ({ value: part.netPrice, currencyIso: "" })),
      ).value;
    } else if (formData[0].optionalParts) {
      const optionalParts = Object.values(formData[0].optionalParts) as OptionalPartFormData[];
      optionalPartsPrice = volatileSum(...optionalParts.map((part) => part.netPrice ?? zero())).value;
    }

    netPrice = optionalPartsPrice ? netPrice + optionalPartsPrice : netPrice;
  }

  const imageUrl = isLinear
    ? productInfo.images.find((image) => image.format === ImageFormat.thumbnail)?.url
    : productInfo.primaryImage?.url;

  return (
    <Stacker gap={16}>
      <Alert severity="success">{t("addToCart.successMessage")}</Alert>
      <div className={classes.content}>
        {!isTablet && <div className={classes.spacer} />}
        <div className={classes.imageContainer}>
          <img src={imageUrl ?? IMG_MISSING_PRODUCT} />
          {hasSales && <SaleTag className={classes.salesTag} size={24} />}
        </div>

        <div className={classes.productInfo}>
          <Stacker gap={8} direction="row">
            {productInfo.preferred && <Icon className={classes.icon} type={IconType.Star} />}
            <p className={classes.designation}>{productInfo.designation}</p>
          </Stacker>
          {isLinear ? (
            <LanguageAwareExternalContent content={productInfo.shortDescription.value} />
          ) : (
            <Translation {...productInfo.shortDescription} />
          )}
        </div>
        <div className={classes.productValues}>
          <SummaryLabel label={t("feedback.option.quantity")} value={formData[0].quantity.toString()} />
          {isBusiness && (
            <>
              <SummaryLabel
                label={t("addToCart.netPricePerPiece")}
                value={
                  netPrice ? (
                    basePrice ? (
                      <div className={classes.salesNetPriceContainer}>
                        <span className={classes.basePrice}>{formatCurrency(basePrice)}</span>
                        <span className={classes.newPrice}>{formatCurrency(netPrice)}</span>
                      </div>
                    ) : (
                      <span className={classNames(hasSales && classes.newPrice)}>{formatCurrency(netPrice)}</span>
                    )
                  ) : (
                    "-"
                  )
                }
              />
              <SummaryLabel
                className="f-bold"
                label={t("wishlistPage.total")}
                value={
                  netPrice ? `${formatCurrency(netPrice * formData[0]?.quantity)}${tariffSurcharge ? "*" : ""}` : "-"
                }
              />
              {tariffSurcharge && <p className="label-small">{t("price.tariff.surcharge.applicable")}</p>}
            </>
          )}
        </div>
      </div>
    </Stacker>
  );
};

const ProductsTable = ({ data }: { data: AddToCartItem[] }) => {
  if (!data) {
    return <Spinner />;
  }
  const { t } = useTranslation();

  const getProductsBasicInfo = (state: RootState) => state.products.basic;
  const productsBasicInfo = useSelector(getProductsBasicInfo);

  const entries = data?.map((productData) => {
    const productInfo = productsBasicInfo[productData.productCode];
    return (
      <TableRow>
        <TableCell>
          <div className={classes.cell}>
            <p className="f-bold m-0 mr-1">{productInfo.designation}</p>
            {productData?.saleNetPrice && <SaleTag size={16} />}
          </div>
        </TableCell>
        <TableCell>
          <p className="m-0">{productData?.quantity}</p>
        </TableCell>
      </TableRow>
    );
  });
  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell>{t("feedback.option.invoiceProduct")}</TableCell>
          <TableCell className={classes.headerLabel}>{t("input.quantity")}</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>{entries}</TableBody>
    </Table>
  );
};

const MultipleEntries = ({
  formData,
}: {
  formData: ExtendedAddToCartFormData[] | AddToCartItem[] | AddToCartApiItem[];
}) => {
  const { t } = useTranslation();
  const { val: table, setTrue: open, setFalse: close } = useBooleanState(true);
  const totalPieces = formData.reduce((accumulator, currentValue) => accumulator + currentValue.quantity, 0);

  return (
    <Stacker gap={16}>
      <Alert severity="success">
        {t("addToCart.multipleProductsAdded", { count: formData.length, pieces: totalPieces })}
      </Alert>
      <div className={classNames(classes.content, classes.multiple)}>
        <LinkButton
          iconBefore={table ? <Icon type={IconType.ChevronUp} /> : <Icon type={IconType.ChevronDown} />}
          className={classes.toggle}
          onClick={() => (table ? close() : open())}
        >
          {table ? t("addToCart.hideTable") : t("addToCart.showTable")}
        </LinkButton>
        {table && <ProductsTable data={formData as AddToCartItem[]} />}
      </div>
    </Stacker>
  );
};

const CartSuccessModal = ({ onClose, formData, linearConfig, className, page }: CartSuccessModalProps) => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  const getSrpFlightStatus = (state: RootState) => state?.srp.inFlight;
  const srpFlightStatus = useSelector(getSrpFlightStatus);

  if (srpFlightStatus) {
    onClose();
  }

  const headerSelector = document.getElementById("root_headerSelector");
  const headerTop = Math.max(0, headerSelector?.getBoundingClientRect().top ?? 0);
  const headerHeight = headerSelector?.offsetHeight ?? 0;

  const productCode = formData[0].productCode;

  const productDescriptor = ProductService.useProductDescriptor({
    productCode: productCode,
    priceReference: getInitialPriceReference(),
    options: { fetch: true },
  });

  //we only need product info if it's for single entries

  const maxFetchedRelatedProducts = 5;

  const getProductBasicInfo = (state: RootState) => state?.products.basic[productCode];

  const productBasicInfo = linearConfig ?? useSelector(getProductBasicInfo);

  const { relatedProducts, state: relatedState } = useMultipleRelatedProducts(
    formData.map((entry) => entry.productCode).slice(0, maxFetchedRelatedProducts),
  );

  const { topsellerProducts, state: topsellerState } = useMultipleTopsellerProducts(
    formData.map((entry) => entry.productCode).slice(0, maxFetchedRelatedProducts),
  );

  const hasMultiple = formData.length > 1;

  const useDialogStyle = makeStyles({
    root: {
      "& .MuiAlert-standard": {
        width: "100%",
        alignSelf: "center",
      },
    },
    actions: {
      display: "grid",
      gridTemplateColumns: "1fr 2fr 2fr 1fr",
      columnGap: isDesktop ? "16px" : isTablet ? "24px" : "8px",
      gridTemplateAreas: isDesktop
        ? `"continue continue cart cart"`
        : isTablet
        ? `". continue cart ."`
        : `"continue continue cart cart"`,
      padding: isTablet ? "16px 24px 0" : "16px",
      borderTop: !isTablet ? `1px solid ${C_OUTLINE_VARIANT}` : "none",
    },
    content: {
      flex: isTablet ? "0" : "",
      overflowY: "visible",
      padding: "0",
    },
    title: {
      padding: "8px 16px",
      display: "flex",
      justifyContent: "end",
      borderBottom: !isTablet ? `1px solid ${C_OUTLINE_VARIANT}` : "",
    },
  });

  const dialogClasses = useDialogStyle();

  // We only show related products on the CartSuccessModal if the product's line is whitelisted
  const visibleRelatedProducts = relatedProducts.filter((product) => product.showPostCartModal);

  let recommendations;
  if (relatedState === "loading" || topsellerState === "loading") {
    recommendations = <CircleSpinner />;
  } else {
    recommendations = (
      <>
        <Recommendations
          entries={visibleRelatedProducts}
          title={t("addToCart.relatedProducts")}
          tracking-aux-reco-type="rsp"
          place={page}
          tracking-aux-title={t("addToCart.relatedProducts", { lng: "en" })}
          tracking-aux-location="add-to-cart-pop-up"
        />
        <Recommendations
          entries={topsellerProducts}
          title={t("addToCart.topsellerProducts")}
          tracking-aux-reco-type="top"
          place={page}
          tracking-aux-title={t("addToCart.topsellerProducts", { lng: "en" })}
          tracking-aux-location="add-to-cart-pop-up"
        />
      </>
    );
  }

  return (
    <StyledDrawer
      anchor={isDesktop ? "right" : "bottom"}
      className={classNames(dialogClasses.root, className)}
      open
      onClose={onClose}
    >
      <Box className={classNames(dialogClasses.title)}>
        <LinkButton type="button" onClick={onClose}>
          <Icon type={IconType.Close} />
        </LinkButton>
      </Box>
      <Box className={dialogClasses.content}>
        <div className={classes.dialog}>
          {hasMultiple ? (
            <MultipleEntries formData={formData} />
          ) : (
            <SingleEntry
              productInfo={productBasicInfo}
              formData={formData as ExtendedAddToCartFormData[]}
              isTablet={isTablet}
              isLinear={!!linearConfig}
              hasRelatedProducts={!!visibleRelatedProducts.length}
            />
          )}
          {!isTablet && <div className={classes.recommendations}>{recommendations}</div>}
        </div>
      </Box>
      {productDescriptor.base && (
        <Box className={classNames(classes.actions, dialogClasses.actions, hasMultiple && classes.multiple)}>
          <Button
            color={isTablet ? "secondary" : "primary"}
            className={classes.continue}
            variant={isTablet ? "contained" : "text"}
            fullWidth
            onClick={onClose}
          >
            {t("notFound.continue")}
          </Button>
          <Button
            href={resolveCartPageUrl()}
            className={classes.cart}
            startIcon={<Icon type={IconType.Cart} />}
            fullWidth
          >
            {t("addToCartPopup.checkoutBtn")}
          </Button>
        </Box>
      )}
      {isTablet && <div className={classes.tabletContainer}>{recommendations}</div>}
    </StyledDrawer>
  );
};

export default CartSuccessModal;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`.${drawerClasses.paper}`]: {
    width: "100wv",
    height: "100dvh",
    [theme.breakpoints.up("desktop-s")]: {
      width: "472px",
    },
    [theme.breakpoints.up("desktop-m")]: {
      width: "528px",
    },
    [theme.breakpoints.up("desktop-l")]: {
      width: "672px",
    },
  },
}));
